import { createContext, useCallback, useContext, useState } from 'react';

import { Box } from '@mui/material';

import CopiedToast, { TOAST_MS } from '@/designs-dev/loft/CopiedToast';

// Editing an exploration follows the Loft rule: the page is read-only, and a
// control copies a command for Claude to run rather than mutating anything
// itself. Text edits here never persist — the edit box is a place to compose
// the new wording, and the copy button hands Claude the exact spec path plus
// the before/after so it can make the real change in the source file.

type Editing = {
  // Where this exploration's spec lives, so a copied command names the file.
  sourceFile: string;
  onCopied: () => void;
  /**
   * Whether the editing affordances are live. Off by default: most of the
   * time an exploration is being read or walked through in a room, and a page
   * where every sentence highlights on hover and every frame is a click-to-
   * copy target reads as an app rather than a set of sketches.
   */
  enabled: boolean;
};

const EditingContext = createContext<Editing>({
  sourceFile: 'the exploration spec',
  onCopied: () => {},
  enabled: false,
});

export const useEditing = () => useContext(EditingContext);

// Builds the command pasted back to Claude. Deliberately verbose about
// location: a spec path alone is ambiguous once frames get reordered, so the
// human label and the exact text being replaced both travel with it.
export const editCommand = ({
  sourceFile,
  path,
  what,
  before,
  after,
}: {
  sourceFile: string;
  path: string;
  what: string;
  before: string;
  after: string;
}): string =>
  [
    `Loft: edit ${path} in ${sourceFile}`,
    '',
    what,
    '',
    'Replace:',
    before,
    '',
    'With:',
    after,
  ].join('\n');

// The folder a spec's frames live in, from its own path. Saves authoring a
// component path on every frame: sketches sit beside the spec by convention.
const specFolder = (sourceFile: string) =>
  sourceFile.slice(0, sourceFile.lastIndexOf('/'));

// A pointer, not an instruction. Clicking a frame copies where it lives —
// the sketch component, the folder, the spec path — and the actual ask gets
// typed after pasting. Keeps the frame click useful for any request rather
// than guessing at one.
export const frameDirection = ({
  sourceFile,
  path,
  id,
  name,
  component,
}: {
  sourceFile: string;
  path: string;
  id: string;
  name: string;
  component?: string;
}): string =>
  [
    `Loft: frame ${id} · ${name}`,
    component
      ? `Sketch: ${component} in ${specFolder(sourceFile)}/`
      : `Sketch: not drawn yet — would live in ${specFolder(sourceFile)}/`,
    `Spec: ${path} in ${sourceFile}`,
    'Copy lives in the .content.ts beside the sketch.',
    '',
  ].join('\n');

export const EditingProvider = ({
  sourceFile,
  enabled,
  children,
}: {
  sourceFile: string;
  enabled: boolean;
  children: React.ReactNode;
}) => {
  const [toast, setToast] = useState(false);

  const onCopied = useCallback(() => {
    setToast(true);
    window.setTimeout(() => setToast(false), TOAST_MS);
  }, []);

  return (
    <EditingContext.Provider value={{ sourceFile, onCopied, enabled }}>
      {children}
      {toast && (
        <Box sx={{ position: 'fixed', top: 24, right: 24, zIndex: 1400 }}>
          <CopiedToast
            accent='#EA6430'
            title='Command copied — paste it to Claude'
            onClose={() => setToast(false)}
          />
        </Box>
      )}
    </EditingContext.Provider>
  );
};
